import React, { useEffect, useState } from 'react';
import {
  Container,
  ContentHeader,
  Title,
  Subtitle,
  StatsRow,
  StatChip,
  StatLabel,
  StatValue,
  SectionCard,
  SectionTitle,
  FormGrid,
  FormRow,
  TableCard,
  TableHeader,
  TableTitle,
  AreaCardsContainer,
  AreaCard,
  AreaCardHeader,
  AreaName,
  AreaCity,
  AreaDetails,
  DetailRow,
  DetailLabel,
  DetailValue,
  TagBadge,
  EmptyStateContainer,
  EmptyStateText,
  ActionButton,
  ContentBody
} from './FoodAreas.styled';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchAllFoodAreas,
  createFoodArea,
  assignAllRestaurants,
  fetchMissingLatLngCount,
  backfillLatLng,
  generateFoodAreas,
  generateFoodAreasFromMaps,
  generateAllFoodAreas,
  generateAllFoodAreasFromMaps,
  triggerNightlyLoad,
  selectFoodAreas,
  selectMissingLatLngCount,
  selectFoodAreasShouldOpen,
  selectFoodAreasMessage,
  selectFoodAreasSeverity,
  selectFoodAreasIsLoading,
  setOpen,
  FoodAreaDTO
} from './slice';
import PageContainer from '../../components/PageContainer/PageContainer';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import {
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
  Backdrop
} from '@mui/material';

const TAG_TYPES = [
  'MOST_WALKABLE',
  'MOST_OPTIONS',
  'LOCAL_FAVORITE',
  'BEST_VALUE',
  'TRENDING'
];

const defaultForm: FoodAreaDTO = {
  city: '',
  name: '',
  description: '',
  centerLat: 0,
  centerLng: 0,
  radiusMeters: 800,
  tagType: 'MOST_OPTIONS',
  score: 0
};

const FoodAreas: React.FC = () => {
  const dispatch = useAppDispatch();
  const foodAreas = useAppSelector(selectFoodAreas);
  const missingLatLngCount = useAppSelector(selectMissingLatLngCount);
  const shouldOpen = useAppSelector(selectFoodAreasShouldOpen);
  const message = useAppSelector(selectFoodAreasMessage);
  const severity = useAppSelector(selectFoodAreasSeverity);
  const isLoading = useAppSelector(selectFoodAreasIsLoading);

  const [form, setForm] = useState<FoodAreaDTO>(defaultForm);
  const [generateCity, setGenerateCity] = useState('');

  useEffect(() => {
    void dispatch(fetchAllFoodAreas());
    void dispatch(fetchMissingLatLngCount());
  }, [dispatch]);

  const setOpenServer = (open: boolean): void => {
    dispatch(setOpen(open));
  };

  const handleFormChange = (field: keyof FoodAreaDTO, value: string | number): void => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateArea = (): void => {
    if (form.city.trim() === '' || form.name.trim() === '') return;
    void dispatch(createFoodArea(form)).then(() => {
      setForm(defaultForm);
    });
  };

  const handleAssignAll = (): void => {
    void dispatch(assignAllRestaurants());
  };

  const handleBackfillLatLng = (): void => {
    void dispatch(backfillLatLng());
  };

  const handleGenerateFoodAreas = (): void => {
    if (generateCity.trim() === '') return;
    void dispatch(generateFoodAreas(generateCity.trim())).then(() => {
      void dispatch(fetchAllFoodAreas());
    });
  };

  const handleGenerateFromMaps = (): void => {
    if (generateCity.trim() === '') return;
    void dispatch(generateFoodAreasFromMaps(generateCity.trim())).then(() => {
      void dispatch(fetchAllFoodAreas());
    });
  };

  const handleGenerateAll = (): void => {
    void dispatch(generateAllFoodAreas());
  };

  const handleGenerateAllFromMaps = (): void => {
    void dispatch(generateAllFoodAreasFromMaps());
  };

  const handleTriggerNightlyLoad = (): void => {
    void dispatch(triggerNightlyLoad());
  };

  return (
    <PageContainer
      title="Food Areas"
      open={shouldOpen}
      setOpen={setOpenServer}
      message={message}
      severity={severity}
    >
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Container>
        <ContentHeader>
          <Title>Food Areas</Title>
          <Subtitle>Manage geographic food areas and assign restaurants to them.</Subtitle>
        </ContentHeader>

        {/* Stats Row */}
        <StatsRow>
          <StatChip>
            <StatLabel>Total Areas</StatLabel>
            <StatValue>{foodAreas.length}</StatValue>
          </StatChip>
          <StatChip>
            <StatLabel>Missing Lat/Lng</StatLabel>
            <StatValue>{missingLatLngCount}</StatValue>
          </StatChip>
          <ActionButton
            variant="outlined"
            onClick={handleBackfillLatLng}
            disabled={isLoading}
          >
            Backfill Lat/Lng
          </ActionButton>
          <ActionButton
            variant="contained"
            color="primary"
            onClick={handleAssignAll}
            disabled={isLoading}
          >
            Assign Restaurants to Areas
          </ActionButton>
          <ActionButton
            variant="outlined"
            color="primary"
            onClick={handleTriggerNightlyLoad}
            disabled={isLoading}
          >
            Trigger Nightly Load
          </ActionButton>
        </StatsRow>

        {/* Auto-Generate Food Areas */}
        <SectionCard>
          <SectionTitle>Auto-Generate Food Areas</SectionTitle>
          <FormRow>
            <TextField
              label="City"
              size="small"
              value={generateCity}
              onChange={(e) => setGenerateCity(e.target.value)}
              placeholder="e.g. Austin"
              sx={{ minWidth: 220 }}
            />
            <ActionButton
              variant="contained"
              color="secondary"
              onClick={handleGenerateFoodAreas}
              disabled={isLoading || generateCity.trim() === ''}
            >
              Generate from DB
            </ActionButton>
            <ActionButton
              variant="outlined"
              color="secondary"
              onClick={handleGenerateFromMaps}
              disabled={isLoading || generateCity.trim() === ''}
            >
              Generate from Maps
            </ActionButton>
          </FormRow>
          <FormRow style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #eee' }}>
            <ActionButton
              variant="contained"
              color="warning"
              onClick={handleGenerateAll}
              disabled={isLoading}
            >
              Generate All Cities (DB)
            </ActionButton>
            <ActionButton
              variant="outlined"
              color="warning"
              onClick={handleGenerateAllFromMaps}
              disabled={isLoading}
            >
              Generate All Cities (Google Maps)
            </ActionButton>
          </FormRow>
        </SectionCard>

        {/* Create Food Area Form */}
        <SectionCard>
          <SectionTitle>Create Food Area</SectionTitle>
          <FormGrid>
            <TextField
              label="City"
              size="small"
              value={form.city}
              onChange={(e) => handleFormChange('city', e.target.value)}
            />
            <TextField
              label="Name"
              size="small"
              value={form.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
            />
            <TextField
              label="Description"
              size="small"
              value={form.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
            />
            <TextField
              label="Center Lat"
              size="small"
              type="number"
              value={form.centerLat}
              onChange={(e) => { const v = parseFloat(e.target.value); handleFormChange('centerLat', isNaN(v) ? 0 : v); }}
            />
            <TextField
              label="Center Lng"
              size="small"
              type="number"
              value={form.centerLng}
              onChange={(e) => { const v = parseFloat(e.target.value); handleFormChange('centerLng', isNaN(v) ? 0 : v); }}
            />
            <TextField
              label="Radius (meters)"
              size="small"
              type="number"
              value={form.radiusMeters}
              onChange={(e) => { const v = parseInt(e.target.value, 10); handleFormChange('radiusMeters', isNaN(v) ? 800 : v); }}
            />
            <FormControl size="small">
              <InputLabel>Tag Type</InputLabel>
              <Select
                value={form.tagType}
                label="Tag Type"
                onChange={(e) => handleFormChange('tagType', e.target.value)}
              >
                {TAG_TYPES.map((tag) => (
                  <MenuItem key={tag} value={tag}>
                    {tag}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Score"
              size="small"
              type="number"
              value={form.score}
              onChange={(e) => { const v = parseFloat(e.target.value); handleFormChange('score', isNaN(v) ? 0 : v); }}
            />
          </FormGrid>
          <FormRow>
            <ActionButton
              variant="contained"
              color="success"
              onClick={handleCreateArea}
              disabled={isLoading || form.city.trim() === '' || form.name.trim() === ''}
            >
              Create Area
            </ActionButton>
          </FormRow>
        </SectionCard>

        {/* Areas List */}
        <ContentBody>
          <TableCard>
            <TableHeader>
              <TableTitle>All Food Areas</TableTitle>
            </TableHeader>

            {foodAreas.length > 0 ? (
              <AreaCardsContainer>
                {foodAreas.map((area) => (
                  <AreaCard key={area.foodAreaId}>
                    <AreaCardHeader>
                      <AreaName>{area.name}</AreaName>
                      <AreaCity>{area.city}</AreaCity>
                    </AreaCardHeader>
                    <AreaDetails>
                      <DetailRow>
                        <DetailLabel>Tag Type</DetailLabel>
                        <DetailValue>
                          <TagBadge>{area.tagType}</TagBadge>
                        </DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Restaurants</DetailLabel>
                        <DetailValue>{area.restaurantCount}</DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Radius</DetailLabel>
                        <DetailValue>{area.radiusMeters}m</DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Score</DetailLabel>
                        <DetailValue>{area.score}</DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Center</DetailLabel>
                        <DetailValue>
                          {area.centerLat.toFixed(5)}, {area.centerLng.toFixed(5)}
                        </DetailValue>
                      </DetailRow>
                      {area.description !== '' && (
                        <DetailRow>
                          <DetailLabel>Description</DetailLabel>
                          <DetailValue>{area.description}</DetailValue>
                        </DetailRow>
                      )}
                    </AreaDetails>
                  </AreaCard>
                ))}
              </AreaCardsContainer>
            ) : (
              <EmptyStateContainer>
                <EmptyStateText>No food areas found</EmptyStateText>
                <EmptyStateText style={{ fontSize: '12px', fontWeight: 400 }}>
                  Create your first food area using the form above.
                </EmptyStateText>
              </EmptyStateContainer>
            )}
          </TableCard>
        </ContentBody>
      </Container>
    </PageContainer>
  );
};

export default withAuthenticationRequired(FoodAreas);
